<template>
  <div>
    <div v-if="isInlineForm">
      <h4 v-if="showTitle" class="mb-2">
        {{ title }}
        <v-icon
          v-if="info"
          tabindex="-1"
          @click="toggleDialog"
        >
          mdi-information
        </v-icon>
      </h4>
      <p v-if="description" class="mb-0">{{ description }}</p>
      <inline-form
        v-model="model"
        :form-fields="inlineFormFields"
        :readonly="quite"
        :auto-continue="autoContinue"
        v-bind="inlineFormParams"
        @autoContinue="onInput"
      >
      </inline-form>
    </div>
    <ValidationProvider
      v-else
      v-slot="{ errors, validated, valid }"
      :name="name"
      ref="validationProvider"
      :rules="disabled ? '' : validation"
    >
      <div
      data-app
      class="form-field"
      v-bind:class="validated && valid && !isEmpty ? 'form-input-valid' : ''"
    >
      <h4 v-if="showTitle" class="mb-2">
        {{ title }}
        <v-badge
          tile
          bordered
          class="ml-1"
          content="from api"
          v-if="quite"
        ></v-badge>
        <v-icon
          v-if="info"
          tabindex="-1"
          @click="toggleDialog"
        >
          mdi-information
        </v-icon>
      </h4>
      <p v-if="description" class="mb-0">{{ description }}</p>
      <div v-if="isTextField">
        <v-text-field
            class="flow-field"
            :type="inputType"
            :name="name"
            :label="label"
            :step="increment"
            :placeholder="placeholder"
            :error-messages="errors"
            :disabled="disabled"
            solo
            @blur="onInput"
            outlined
            flat
            dense
            :readonly="quite"
            v-model="model"
            v-bind="vuetifyProps"
        >
          <template v-if="iconErrorMessage" v-slot:message>
            <v-icon color="error">{{iconErrorMessage}}</v-icon>
            {{ errors[0] }}
          </template>
          <template v-if="clearButton && clearButtonSlot === 'append' " v-slot:append>
            <v-btn
              text
              depressed
              class="inline-row-clear-button"
              @click="clearButtonAction">
              {{ clearButtonTitle }} <v-icon right>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
          <template v-else-if="clearButton && clearButtonSlot === 'append-outer' " v-slot:append-outer>
            <v-btn
              text
              depressed
              class="inline-row-clear-button"
              @click="clearButtonAction">
              {{ clearButtonTitle }} <v-icon right>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </div>
      <div v-else-if="isTextArea">
        <v-textarea
          :name="name"
          :label="label"
          :placeholder="placeholder"
          :disabled="disabled"
          solo
          outlined
          flat
          dense
          @blur="onInput"
          :error-messages="errors"
          :readonly="quite"
          v-model="model"
          v-bind="vuetifyProps"
        >
          <template v-if="iconErrorMessage" v-slot:message>
            <v-icon color="error">{{iconErrorMessage}}</v-icon>
            {{ errors[0] }}
          </template>
        </v-textarea>
      </div>
      <v-radio-group
        v-else-if="isRadios"
        @change="onInput"
        column
        v-model="model"
        validate-on-blur
        v-bind="vuetifyProps"
        :row="inline"
        :rules="[validateRadio]"
        :error-messages="errors"
        :readonly="quite"
        :disabled="disabled"
      >
        <template v-slot:default>
          <v-radio
            v-bind:class="choice.img ? 'selection-image-card' : 'input-bordered'"
            v-for="choice in fieldChoices"
            :name="choice.label"
            :key="choice.value"
            v-bind="choice"
          >
            <template v-if="choice.img" v-slot:label>
              <v-card
                max-width="150px"
                flat
                tile
              >
                <v-img
                  class="material-image"
                  :src="choice.img"
                  aspect-ratio="1"
                >
                  <template v-slot:placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="grey lighten-5"
                      ></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
                <v-card-subtitle class="pl-0 pb-0">
                  {{ choice.label }}
                </v-card-subtitle>
              </v-card>
            </template>
            <template v-else-if="choice.icon" v-slot:label>
              <v-icon left>{{choice.icon}}</v-icon>
              {{ choice.label }}
            </template>
          </v-radio>
        </template>
        <template v-if="iconErrorMessage" v-slot:message>
          <v-icon color="error">{{iconErrorMessage}}</v-icon>
          {{ errors[0] }}
        </template>
      </v-radio-group>
      <div
        v-bind:class="{inline}"
        v-else-if="isCheckboxes"
      >
        <h4 v-if="title" class="mb-2">
          {{title}}
        </h4>
        <v-checkbox
          v-for="choice in fieldChoices"
          :key="choice.value"
          v-model="model"
          v-bind="vuetifyProps"
          :true-value="true"
          :false-value="false"
          :disabled="disabled"
          :readonly="quite"
          @click="onInput"
          class="pa-0"
        >
          <template v-slot:label>
            <template v-if="choice.img">
              <v-img :src="choice.img"/>
            </template>
            <template v-if="choice.icon">
              <v-icon left>{{choice.icon}}</v-icon>
            </template>
            {{ choice.label }}
            <v-icon
              v-if="choice.info"
              class="ml-1"
              @click.stop="toggleDialog($event, choice.info)"
            >
              mdi-information
            </v-icon>
          </template>
          <template v-if="iconErrorMessage" v-slot:message>
            <v-icon color="error">{{iconErrorMessage}}</v-icon>
            {{ errors[0] }}
          </template>
        </v-checkbox>
      </div>
      <v-row
        v-else-if="isCards"
      >
        <v-col
          cols="12"
          sm="6"
          lg="3"
          class="d-flex"
          v-for="(choice, n) in fieldChoices"
          :key="n"
        >
          <v-checkbox
            v-model="model"
            class="hide-checkbox-icon"
            @click="onInput"
            :true-value="choice.value"
            :false-value="null"
            :multiple="multiple"
            v-bind="vuetifyProps"
            :disabled="disabled"
            :readonly="quite"
            inlist
          >
            <template v-slot:label>
              <checkbox-card
                :id="choice.value"
                :title="choice.title"
                :label="choice.label"
                :selected="isCheckboxSelected(choice.value)"
                :multiple="multiple"
                v-bind="choice"
               >
              </checkbox-card>
            </template>
            <template v-if="iconErrorMessage" v-slot:message>
              <v-icon color="error">{{iconErrorMessage}}</v-icon>
              {{ errors[0] }}
            </template>
          </v-checkbox>
        </v-col>
      </v-row>
      <div v-else-if="isSelect">
        <v-select
            v-model="model"
            :items="fieldChoices"
            :error-messages="errors"
            @change="onInput"
            :readonly="quite"
            :disabled="disabled"
            item-text="label"
            solo
            outlined
            flat
            dense
            v-bind="vuetifyProps"
        >
          <template v-if="iconErrorMessage" v-slot:message>
            <v-icon color="error">{{iconErrorMessage}}</v-icon>
            {{ errors[0] }}
          </template>
          <template v-if="clearButton && clearButtonSlot === 'append' " v-slot:append>
            <v-btn
              text
              depressed
              class="inline-row-clear-button"
              @click="clearButtonAction">
              {{ clearButtonTitle }} <v-icon right>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
          <template v-else-if="clearButton && clearButtonSlot === 'append-outer' " v-slot:append-outer>
            <v-btn
              text
              depressed
              class="inline-row-clear-button"
              @click="clearButtonAction">
              {{ clearButtonTitle }} <v-icon right>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
        </v-select>
      </div>
      <div v-else-if="isSlider">
        <v-slider
          v-model="model"
          inverse-label
          :error-messages="errors"
          :label="String(model)"
          :min="min"
          :max="max"
          :step="increment"
          v-bind="vuetifyProps"
          @change="onInput"
        >
        </v-slider>
      </div>
      <div v-else-if="isSliderWithButtons">
        <slider-with-buttons
          :error-messages="errors"
          :min="min"
          :max="max"
          :increment="increment"
          :vuetifyProps="vuetifyProps"
          :disableTextInput="disableTextInput"
          :amountOfDecimals="amountOfDecimals"
          @change="onInput"
          @blur="onInput"
          v-model="model"
        ></slider-with-buttons>
      </div>
      <div v-else-if="isAutocomplete">
        <v-autocomplete
            v-model="model"
            :label="label"
            :readonly="quite"
            cache-items
            solo
            dense
            outlined
            flat
            :disabled="disabled"
            @change="onInput"
            hide-no-data
            :error-messages="errors"
            :items="autocompleteItems"
            v-bind="vuetifyProps"
        >
          <template v-if="iconErrorMessage" v-slot:message>
            <v-icon color="error">{{iconErrorMessage}}</v-icon>
            {{ errors[0] }}
          </template>
          <template v-if="clearButton && clearButtonSlot === 'append' " v-slot:append>
            <v-btn
              text
              depressed
              class="inline-row-clear-button"
              @click="clearButtonAction">
              {{ clearButtonTitle }} <v-icon right>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
          <template v-else-if="clearButton && clearButtonSlot === 'append-outer' " v-slot:append-outer>
            <v-btn
              text
              depressed
              class="inline-row-clear-button"
              @click="clearButtonAction">
              {{ clearButtonTitle }} <v-icon right>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
        </v-autocomplete>
      </div>
      <div v-else-if="isDatePicker">
          <v-menu
            v-model="showPicker"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="model"
                :label="label"
                :prepend-icon="vuetifyProps && vuetifyProps['prepend-icon'] || 'mdi-calendar'"
                readonly
                solo
                outlined
                flat
                dense
                :disabled="disabled"
                :error-messages="errors"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="model"
              @input="onPickerInput"
            ></v-date-picker>
        </v-menu>
      </div>
      <div v-else-if="isTimePicker">
        <v-dialog
          ref="dialog"
          v-model="showPicker"
          :return-value.sync="model"
          persistent
          width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="model"
              :label="label"
              :prepend-icon="vuetifyProps && vuetifyProps['prepend-icon'] || 'mdi-clock-time-four-outline'"
              readonly
              solo
              outlined
              flat
              dense
              :disabled="disabled"
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-time-picker
            v-if="showPicker"
            v-model="model"
            full-width
            ampm-in-title
            format="ampm"
            @input="onPickerInput"
            v-bind="vuetifyProps"
          >
            <v-spacer></v-spacer>
            <v-btn
              text
              color="primary"
              @click="showPicker = false"
            >
              {{ $t('Cancel') }}
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="$refs.dialog.save(model)"
            >
              {{ $t('OK') }}
            </v-btn>
          </v-time-picker>
        </v-dialog>
      </div>
      <div v-else-if="isPriceField">
        <v-text-field
            class="flow-field"
            :type="inputType"
            :name="name"
            :label="label"
            :placeholder="placeholder"
            :error-messages="errors"
            solo
            outlined
            flat
            dense
            :disabled="disabled"
            :readonly="quite"
            v-bind="vuetifyProps"
            v-model="priceFieldModel"
            @blur="onPriceFieldInput"
            @focus="isPriceFieldActive = true"
        >
          <template v-if="iconErrorMessage" v-slot:message>
            <v-icon color="error">{{iconErrorMessage}}</v-icon>
            {{ errors[0] }}
          </template>
          <template v-if="clearButton && clearButtonSlot === 'append' " v-slot:append>
            <v-btn
              text
              depressed
              class="inline-row-clear-button"
              @click="clearButtonAction">
              {{ clearButtonTitle }} <v-icon right>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
          <template v-else-if="clearButton && clearButtonSlot === 'append-outer' " v-slot:append-outer>
            <v-btn
              text
              depressed
              class="inline-row-clear-button"
              @click="clearButtonAction">
              {{ clearButtonTitle }} <v-icon right>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </div>
      <div v-else-if="isVuexWidgetState">
        <vuex-state-widget
          @input="onInput"
          v-model="model"
          v-bind="vuexWidgetParams"
        ></vuex-state-widget>
      </div>
      <div class="my-3" v-else>
        <h5>Please fix your field JSON scheme</h5>
      </div>
      <card-info-dialog
          :show-dialog="showInformPopup"
          @closeDialog="toggleDialog"
      >
        {{infoText}}
      </card-info-dialog>
    </div>
  </ValidationProvider>
  </div>

</template>

<script>
  import {ValidationProvider} from "vee-validate";
  import {_} from 'vue-underscore';
  import {ComponentWithModelMixin, FlowGeneratorMixin} from "../../mixins/FlowGenerator";
  import {FlowGeneratorBus} from "../../plugins/bus";
  import CheckboxCard from "../Widgets/CheckboxCard";
  import InlineForm from "../Widgets/InlineForm";
  import SliderWithButtons from "../Widgets/SliderWithButtons";
  import CardInfoDialog from "../Widgets/CardInfoDialog";
  import VuexStateWidget from "../Widgets/VuexStateWidget";

  export default {
    name: "FlowField",
    props: {
      title: {
        type: String,
        required: true,
        default: '',
        description: "Question title"
      },
      description: {
        type: String,
        required: false,
        description: "Additional description for question"
      },
      type: {
        type: String,
        required: true,
        description: "Pick input widget that should be rendered on form"
      },
      inputType: {
        type: String,
        default: "text",
        description: "HTML5 input type specification, like number, text, email, password, etc..."
      },
      name: {
        type: String,
        required: true,
        description: "Value of input's name attribute, used as key of form data object"
      },
      flowName: {
        type: [String, undefined],
        description: "The name of the flow schema, use this to distinguish valid-flow-field-input events"
      },
      label: {
        type: String,
        description: "Field label"
      },
      helpText: {
        type: String,
        description: "Showed below input field as tip"
      },
      calculate: {
        type: [Function, undefined],
        description: "The property was made for function to make calculation for calculated fields"
      },
      default: {
        type: [String, Boolean, Number, undefined],
        default: undefined,
        description: "Just default value for the field"
      },
      placeholder: {
        type: String,
        description: "Input placeholder, showed when field supports it"
      },
      choices: {
        type: [Array, undefined],
        default: undefined,
        description: "Strict choices defined in schema"
      },
      inline: {
        type: Boolean,
        default: false,
        description: "Using to show checkboxes, radios in inline style"
      },
      showResult: {
        type: Boolean,
        default: false,
        description: "When 'true' - will show result of checking answer: Wrong or Right"
      },
      choicesGetter: {
        type: String,
        description: "Vuex path to getter that returns items for autocomplete or other input with choices"
      },
      validation: {
        type: [Object, String],
        default: 'required',
        description: "Vee-validation rules. How to? - Read vee-validate documentation"
      },
      vuetifyProps: {
        type: Object,
        default: () => ({}),
        description: "Bind this object data to props of vuetify component. See property specification for used component in vuetify docs."
      },
      flattenModel: {
        type: Object,
        default: () => ({}),
        description: "Flatten value of model, redirected from FlowPreview to have filtered by `when` directive"
      },
      quite: {
        type: Boolean,
        default: false,
        description: "Make field invisible on UI but it still exists in model. Could be used with default property to have some predefined and invisible value in your flow"
      },
      smartChoices: {
        type: Boolean,
        default: false,
        description: "Pick widget with smart logic for any select or radio types in question. Radio should be rendered if choices e" +
          "length less 10, instead select"
      },
      onChangeEvent: {
        type: [Function, undefined],
        default: undefined,
        description: "To handle onChange event on the field and implement custom logic for it."
      },
      autoContinue: {
        type: Boolean,
        default: false,
        description: "If true onBlur event of this field will send autoContinue event into FlowPreview",
      },
      multiple: {
        type: Boolean,
        default: false,
        description: "Control a multiple mode for checkbox widgets",
      },
      iconErrorMessage: {
        type: [String, undefined],
        default: "mdi-alert-circle",
        description: "An extra info which will append before input's error-message. Could be in a html format."
      },
      id: {
        type: [String, undefined],
        default: undefined
      },
      inlineFormFields: {
        type: Object
      },
      inlineFormParams: {
        type: Object
      },
      min: {
        type: Number,
        default: 1
      },
      max: {
        type: Number,
        default: 100
      },
      increment: {
        type: [String, Number],
        default: 1
      },
      disableTextInput: {
        type: Boolean,
        default: false
      },
      amountOfDecimals: {
        type: Number,
        default: 0
      },
      info: {
        type: String,
        default: ''
      },
      enabledWhen: {
        type: [Array, Object, undefined],
        default: undefined,
        description: "Using to make field disabled behaviour, with `when` directive and same logic."
      },
      clearButton: {
        type: Boolean,
        default: false
      },
      clearButtonSlot: {
        type: String,
        default: 'append-outer'
      },
      clearButtonTitle: {
        type: String,
        default: "Clear"
      },
      clearButtonAction: {
        type: Function,
        default: () => ({})
      },
      vuexWidgetParams: {
        type: Object
      },
      isInlineFormField: {
        type: Boolean,
        default: false
      }
    },
    mixins: [ComponentWithModelMixin, FlowGeneratorMixin],
    components: {
      VuexStateWidget,
      SliderWithButtons,
      InlineForm,
      CheckboxCard,
      ValidationProvider,
      CardInfoDialog,
    },
    computed: {
      smartRadio() {
        return this.smartChoices && this.fieldChoices.length < 10 && !this.isSelect
      },

      smartSelect() {
        return this.smartChoices && this.fieldChoices.length >= 10 && !this.isRadios
      },

      isRadios() {
        return this.smartRadio || (!this.smartChoices && this.type === 'radio')
      },

      isSelect() {
        return this.smartSelect || (!this.smartChoices && this.type === 'select')
      },

      isCards() {
        return this.type === 'card'
      },

      isCheckboxes() {
        return this.type === 'checkbox'
      },

      isTextField() {
        return this.type === 'text'
      },

      isPriceField() {
        return this.type === 'price'
      },

      isTextArea() {
        return this.type === 'textarea'
      },

      isInlineForm() {
        return this.type === 'inlineForm'
      },

      isDatePicker() {
        return this.type === 'date'
      },

      isTimePicker() {
        return this.type === 'time'
      },

      isSliderWithButtons() {
        return this.type === 'sliderWithButtons'
      },

      isSlider() {
        return this.type === 'slider'
      },

      isAutocomplete() {
        return this.type === 'autocomplete'
      },

      isVuexWidgetState() {
        return this.type === 'vuexStateWidget'
      },
      autocompleteItems() {
        return this.fieldChoices
      },
      // hide title for checkboxes
      showTitle() {
        return !this.isCheckboxes
      },
      disabled() {
        return Boolean(
          (!!this.enabledWhen && !this.isMatchWhen(this.flattenModel, this.enabledWhen))
          || (this.vuetifyProps && this.vuetifyProps.disabled)
        )
      },
      // return choices from vuex when it possible
      // or return strict values from schema
      fieldChoices() {
        let fromVuex;
        if (this.$store && this.choicesGetter) {
          try {
            fromVuex = this.$store.getters[this.choicesGetter]
          } catch (e) {
            console.error(e)
          }
        }
        if (fromVuex && fromVuex.length > 0) {
          return fromVuex
        } else if (this.choices && this.choices.length > 0) {
          return this.choices
        } else {
          return []
        }
      },
      isCheckboxSelected() {
        return value => (this.multiple && _.contains(this.model, value)) || this.model === value
      },
      priceFieldModel: {
        get: function () {
          if (this.model) {
            if (this.isPriceFieldActive) {
              // Cursor is inside the input field
              return this.model.toString()
            } else {
              // Get value and create template with commas
              this.model.toString()
              let val = this.model.toFixed(2)
                .replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, "$1,")
                .replace(/,/g, '.')
              // Find last '.'
              let lastDot = val.lastIndexOf('.')
              let arr = val.split('')
              // Rebase last '.' to ','
              arr[lastDot] = ',';
              return arr.join('')
            }
          }
          return null
        },
        set: function (modifiedValue) {
          // Recalculate values
          let newValue = parseFloat(modifiedValue.replace(/[^\d.]/g, ""))
          // Ensure that it is not NaN
          if (isNaN(newValue)) {
            newValue = 0
          }
          this.model = newValue
        }
      },
      isEmpty() {
        return this.model === "" || this.model === undefined || this.model === null || this.model === 0
      }
    },
    data() {
      return {
        model: null,
        showPicker: false,
        isPriceFieldActive: false,
        showInformPopup: false,
        infoText: ''
      }
    },
    mounted() {
      this.setInitials()
      this.checkSpec()
      FlowGeneratorBus.$on(`show-error-${this.id}`, ([value, scrollTo = false]) => {
        this.$refs.validationProvider.applyResult({
          errors: [value],
          valid: false,
          failedRules: {}
        });
        if (scrollTo) {
          this.$vuetify.goTo(this.$refs.validationProvider)
        }
      })
    },
    beforeDestroy() {
      FlowGeneratorBus.$off(`show-error-${this.id}`)
    },
    watch: {
      'flattenModel': function (val) {
        if (this.calculate) {
          const calculatedModelValue = this.calculate(val)
          if (calculatedModelValue) this.model = calculatedModelValue
        }
      },
      'disabled': function () {
        this.$refs.validationProvider.reset()
        this.setInitials(true)
      },
      'default': function () {
        this.$refs.validationProvider.reset()
        this.setInitials(true)
      }
    },
    methods: {
      emitter(val) {
        this.$emit("input", val)
        if (this.onChangeEvent) {
          this.onChangeEvent(val)
        }
      },
      validateRadio(val) {
        if (_.isEmpty(val)) {
          return this.$i18n.t('Please pick the one of choices')
        } else {
          return true
        }
      },
      async onPickerInput() {
        this.showPicker = false
        await this.onInput()
      },
      async isValid() {
        if (!this.validation || !this.$refs.validationProvider) {
          // return valid if no validation provider or no validation
          return true
        }
        const validation = await this.$refs.validationProvider.validateSilent()
        let isValid = validation.valid
        // now some custom validation which can override the validation provider
        if (!this.isEmpty && (this.isRadios || this.isCheckboxes)) {
          isValid = true
        } else if (this.isCards) {
          isValid = !this.isEmpty
        }
        return isValid
      },
      async onInput() {
        const valid = await this.isValid()
        if (valid && this.id && !this.isInlineFormField) {
          // emit only if field is valid or validation is missing (always validate)
          FlowGeneratorBus.$emit(
            "valid-flow-field-input",
            {flowName: this.flowName, id: this.id, model: this.model}
          )
        }
        if (this.autoContinue) this.$emit('autoContinue')
      },
      async onPriceFieldInput() {
        this.isPriceFieldActive = false
        await this.onInput()
      },
      async setInitials(clean = false) {
        // safely set defaults in the model after the dom has been rendered
        await this.$nextTick()
        if (!clean && this.value !== undefined) {
          this.model = this.value
        } else if (this.default !== undefined) {
          this.model = this.default
        } else if (this.isTextArea || this.isTextField) {
          this.model = ""
        } else if (this.isRadios || this.isCheckboxes) {
          this.model = null
        } else if (this.isInlineForm) {
          this.model = [{}]
        } else if (this.isPriceField) {
          this.model = 0
        }
        // we need to wait for the nextTick to give the ValidationProvider
        // some time to handle the model change
        await this.$nextTick()
        await this.onInput()
      },
      // To notify developer that schema for the field is not correct
      // or has recommendation
      checkSpec() {
        // warning for autocomplete field
        if (this.isAutocomplete && !this.choices) {
          if (!this.$store || !this.choicesGetter) {
            console.warn("For autocomplete field you should set `choices`" +
              " or define `choicesGetter` which could get choices from vuex store (if you are using it).")
          }
        }
        if (!!this.onChangeEvent && this.onChangeEvent.length === 0) {
          console.warn("Your onChangeEvent should get at least one parameter")
        }
        if (this.isInlineForm) {
          if (!this.inlineFormFields) {
            console.warn("Your inline form should have inlineFormFields defined")
          }
        }
      },
      toggleDialog(event, value) {
        this.infoText = value || this.info
        this.showInformPopup = !this.showInformPopup
      }
    }
  }
</script>

<style lang="scss">
  .form-field {
    .v-input--selection-controls {
      margin-top: 8px;
    }
  }

  .hide-checkbox-icon {
    flex: 1 !important;

    .v-input--selection-controls__input {
      display: none;
    }
  }
</style>
